/**
 * Map enter-bill / create-bill API failures to clear alert copy for end users.
 * Use [[Label]] in message text for clickable navigation (handled by ErrorModal).
 */

import { hasCompressibleAttachments } from './attachmentCompression';

const BYTES_PER_MB = 1024 * 1024;

/** Combined on-disk size. Base64 plus the rest of the bill must stay inside the 50 MB request limit. */
export const BILL_ATTACHMENT_FILE_LIMIT_BYTES = 37 * BYTES_PER_MB;

export function getAttachmentsTotalBytes(attachments) {
  return (attachments || []).reduce(
    (sum, att) => sum + (Number(att && att.fileSize) || 0),
    0
  );
}

export function attachmentsExceedBillLimit(attachments) {
  return getAttachmentsTotalBytes(attachments) > BILL_ATTACHMENT_FILE_LIMIT_BYTES;
}

/** Rounds up so a total just over the limit never displays as equal to it. */
export function formatMbUp(bytes) {
  const mb = Math.ceil((bytes / BYTES_PER_MB) * 10) / 10;
  return Number.isInteger(mb) ? String(mb) : mb.toFixed(1);
}

export function buildBillAttachmentLimitMessage(totalBytes) {
  const limitMb = formatMbUp(BILL_ATTACHMENT_FILE_LIMIT_BYTES);
  const total = Number(totalBytes) || 0;

  if (total <= 0) {
    return [
      'The attached files are too large to save with this bill.',
      `Limit: ${limitMb} MB`,
    ].join('\n');
  }

  return [
    total <= BILL_ATTACHMENT_FILE_LIMIT_BYTES
      ? 'This bill is too large to save.'
      : 'The attached files are too large to save with this bill.',
    `Uploaded: ${formatMbUp(total)} MB  |  Limit: ${limitMb} MB`,
  ].join('\n');
}

export function describeAttachmentCompression(beforeBytes, afterBytes, { compressedCount, failedCount }) {
  const parts = [];
  if (compressedCount) {
    parts.push(`Reduced from ${formatMbUp(beforeBytes)} MB to ${formatMbUp(afterBytes)} MB`);
    if (afterBytes > BILL_ATTACHMENT_FILE_LIMIT_BYTES) {
      parts.push(`still over the ${formatMbUp(BILL_ATTACHMENT_FILE_LIMIT_BYTES)} MB limit`);
    } else {
      parts.push('Review the files, then save the bill');
    }
  } else {
    parts.push('These files could not be made smaller');
  }
  if (failedCount) {
    parts.push(`${failedCount} file${failedCount === 1 ? '' : 's'} could not be compressed`);
  }
  return parts.join(' · ');
}

export function resolveBillSaveAlert(err, { attachments } = {}) {
  const data = err?.response?.data || {};
  const status = Number(err?.response?.status);
  const code = String(data.code || '').trim();
  const raw = String(data.error || data.message || err?.message || 'Could not save this bill.').trim();
  const axiosStatusText = /^request failed with status code \d+$/i.test(String(err?.message || ''));
  const tooLarge = status === 413
    || code === 'ATTACHMENTS_TOO_LARGE'
    || /entity too large|payload too large|attachments are too large/i.test(raw)
    || (
      !data.error
      && (status === 413 || status === 500)
      && (axiosStatusText || /something went wrong/i.test(raw))
    );

  if (tooLarge) {
    const canCompress = hasCompressibleAttachments(attachments);
    return {
      title: 'Unable to save bill',
      message: buildBillAttachmentLimitMessage(getAttachmentsTotalBytes(attachments)),
      actionKey: canCompress ? 'compress-files' : null,
      actionButtonText: canCompress ? 'Compress files' : '',
    };
  }
  const draftNo =
    data.draftBillNo
    || (raw.match(/draft bill\s+(\S+)/i) || [])[1]
    || '';

  if (
    code === 'TIMESHEET_ON_DRAFT'
    || /already on draft bill/i.test(raw)
  ) {
    const billLabel = draftNo || 'an existing draft';
    return {
      title: 'Draft already exists',
      message: [
        `This consultant timesheet is already on draft bill ${billLabel}.`,
        '',
        'Open [[Drafts & Captured]] and continue from that draft. Creating another bill for the same timesheet is not allowed.',
      ].join('\n'),
      actionKey: 'drafts-captured',
    };
  }

  if (
    code === 'TIMESHEET_PORTAL_SUBMITTED'
    || /vendor portal/i.test(raw)
  ) {
    return {
      title: 'Already submitted by vendor',
      message: [
        'This timesheet was already submitted from the vendor portal.',
        '',
        'Open [[Invites & Submitted]] to review it and finish the bill there.',
      ].join('\n'),
      actionKey: 'invites-submitted',
    };
  }

  if (
    code === 'TIMESHEET_ALREADY_BILLED'
    || /already (used )?on another bill/i.test(raw)
  ) {
    return {
      title: 'Timesheet already billed',
      message: [
        'This timesheet is already linked to another bill.',
        '',
        'Open that bill instead of creating a new one for the same timesheet.',
      ].join('\n'),
      actionKey: null,
    };
  }

  return {
    title: 'Could not save bill',
    message: raw || 'Something went wrong while saving. Please try again.',
    actionKey: null,
  };
}
